import { useState } from 'react';
import { S } from '../../domain/state.js';
import { fMoney, wDYY, todayISO, iso10 } from '../../lib/format.js';
import { savCfg, savFrom, savUnassigned } from '../../domain/savings.js';
import { savPeriod, exitPlan } from '../../domain/plan.js';
import { monthlyNet } from '../../domain/forecast.js';
import { wTx } from '../../domain/explain.js';
import { Xn, Sheet, Empty, confirmDialog, promptDialog } from '../../components/ui.jsx';
import { Icon } from '../../components/Icon.jsx';
import { openModal, closeModal } from '../../app/nav.js';
import { useApp, toast } from '../../store/ui.js';
import { saveSavingsGoal, addToSavingsGoal, deleteSavingsGoal, assignToSavingsGoal } from '../../store/actions.js';

export function openGoalForm(id) {
  openModal(() => <GoalForm id={id} />);
}

export function GoalsPage() {
  useApp();
  const SP = savPeriod(), P = S.set.balDate ? exitPlan() : null;
  const cap = P ? P.surplus : Math.round(monthlyNet());
  const total = SP.goals.reduce((s, x) => s + x.total, 0);
  return (
    <div className="stack">
      <div className="period">
        <div className="card-h">
          <div className="sec" style={{ margin: 0 }}>מצב החיסכון</div>
          <button className="ubtn ubtn-primary ubtn-sm" onClick={() => openGoalForm()}><Icon name="plus" size={16} />יעד חדש</button>
        </div>
        <div className="grid2">
          <div className="sav-box"><div className="sav-k">נחסך בכל היעדים</div><div className="sav-v"><Xn k="sav_total">{fMoney(total)}</Xn></div></div>
          <div className="sav-box"><div className="sav-k">הועבר לחיסכון בתקופה</div><div className="sav-v"><Xn k="sav_month" arg="done">{fMoney(SP.done)}</Xn></div>
            {SP.M > 0 && <div className="sav-s">מתוך {fMoney(SP.M)} לפי היעדים</div>}</div>
        </div>
        <div className="sub mt10">
          {cap > 0
            ? SP.M > cap ? <b className="red">היעדים דורשים {fMoney(SP.M)} בחודש, יותר מהעודף (<Xn k="surplus">{fMoney(cap)}</Xn>). כדאי לדחות תאריך או להקטין סכום.</b>
              : <>עודף בחודש רגיל: <Xn k="surplus">{fMoney(cap)}</Xn>{SP.M ? '. היעדים דורשים ' + fMoney(SP.M) + '.' : '. יעד סביר להתחלה: עד ' + fMoney(Math.floor((cap * 0.7) / 100) * 100) + ' בחודש.'}</>
            : 'בחודש רגיל אין עודף, ולכן כרגע אין ממה לחסוך.'}
          {P && P.stage < 4 && <div className="note mt6">קודם המטרה במסך הבית (שלב {P.stage} מתוך 4). כסף שעובר לחיסכון לפני כן מוריד את העו"ש.</div>}
        </div>
      </div>
      {SP.goals.length ? <div className="goals-grid">{SP.goals.map((s) => <GoalCard key={s.g.id} s={s} />)}</div>
        : <div className="period"><Empty emo="🌱">אין יעדי חיסכון עדיין<br /><small className="muted">יעד יכול להיות חופשה, רכב, או קרן חירום</small></Empty></div>}
      <Unassigned goals={SP.goals} />
    </div>
  );
}

function GoalCard({ s }) {
  const g = s.g, col = g.color || 'var(--green)';
  const dl = g.deadline ? iso10(g.deadline) : null;
  let line;
  if (s.done) line = <b className="green">✓ היעד הושג</b>;
  else if (s.monthly) line = <>כדי להגיע{dl ? ' עד ' + wDYY(dl) : ''}: <Xn k="sav_req" arg={g.id}>{fMoney(s.monthly)}</Xn> בחודש</>;
  else if (s.eta) line = 'בקצב של ' + fMoney(s.avg) + ' בחודש: בערך ב-' + wDYY(s.eta);
  else line = 'אין תאריך יעד וסכום חודשי. ערוך כדי לקבל המלצה.';
  async function addManual() {
    const v = await promptDialog({ title: 'הוספה ידנית ליעד', type: 'number', okLabel: 'הוסף',
      body: 'רק לכסף שלא עובר דרך העו"ש, למשל מזומן או הפקדה ישירה מהמשכורת. העברות מהעו"ש נספרות לבד בסריקה.' });
    const amt = parseFloat(v);
    if (!(amt > 0)) return;
    try { await addToSavingsGoal(g.id, amt); } catch (e) { toast('⚠️ ' + e.message); }
  }
  return (
    <div className="goal-card">
      <div className="goal-card-top">
        <div className="goal-card-ico" style={{ background: 'color-mix(in srgb,' + col + ' 15%,transparent)' }}>{g.emoji}</div>
        <div className="goal-card-info">
          <div className="goal-card-name">{g.name}{s.done ? ' 🎉' : ''}</div>
          <div className="goal-card-deadline">{s.kw.length ? '🔗 מזהה העברות עם: ' + s.kw.join(', ') : '⚠️ אין מילים לזיהוי, רק הוספה ידנית'}</div>
        </div>
        <div className="goal-card-amounts">
          <div className="goal-card-saved" style={{ color: col }}><Xn k="sav_goal" arg={g.id}>{fMoney(s.total)}</Xn></div>
          <div className="goal-card-target">מתוך {fMoney(s.target)}</div>
        </div>
      </div>
      <div className="goal-progress"><div className="goal-progress-fill" style={{ width: s.pct + '%', background: col }} /></div>
      <div className="row-b mb8" style={{ fontSize: '.86rem', color: 'var(--ink2)' }}><span>{line}</span><span className="nowrap muted">{Math.round(s.pct)}%</span></div>
      <div className="goal-actions">
        <button className="ubtn ubtn-outline" style={{ flex: 1 }} onClick={() => openGoalForm(g.id)}><Icon name="edit" size={16} />עריכה</button>
        <button className="ubtn ubtn-outline" style={{ flex: 1 }} onClick={addManual}><Icon name="plus" size={16} />הוספה ידנית</button>
      </div>
    </div>
  );
}

function Unassigned({ goals }) {
  const un = savUnassigned();
  if (!un.length) return null;
  return (
    <div className="period">
      <div className="sec" style={{ marginBottom: 4 }}>העברות לחיסכון שלא שויכו ({un.length})</div>
      <div className="note mb8">זוהו בסריקת העו"ש. הן לא נספרות כהוצאה. שייך אותן ליעד, וההעברות הבאות מאותו סוג ישויכו לבד.</div>
      {un.slice(0, 20).map((t) => {
        const x = wTx(t);
        return (
          <div className="why-li" key={t.id}>
            <div style={{ minWidth: 0 }}><div className="why-ln">{x.n}</div><div className="why-ls">{x.d}</div></div>
            <div className="row" style={{ gap: 6 }}>
              <b dir="ltr">{fMoney(-x.v)}</b>
              {goals.length > 0 && (
                <select className="sel" style={{ maxWidth: 110 }} value="" onChange={(e) => e.target.value && assignToSavingsGoal(t, Number(e.target.value))}>
                  <option value="">שייך…</option>
                  {goals.map((s) => <option key={s.g.id} value={s.g.id}>{s.g.emoji} {s.g.name}</option>)}
                </select>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GoalForm({ id }) {
  const g = id != null ? S.savings.find((x) => x.id === id) : null;
  const c = g ? savCfg(g.id) : {};
  const [f, setF] = useState({
    name: g ? g.name : '', target: g ? g.target : '', emoji: g ? g.emoji : '', deadline: g && g.deadline ? iso10(g.deadline) : '',
    kw: g ? c.kw || '' : 'פיקדון', monthly: c.monthly || '', base: g ? Number(g.saved) || '' : '', from: g ? savFrom(g) : todayISO(),
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function save() {
    const target = parseFloat(f.target);
    if (!f.name.trim() || !(target > 0)) return toast('נא למלא שם וסכום יעד');
    try {
      await saveSavingsGoal(g ? g.id : null,
        { name: f.name.trim(), target, saved: parseFloat(f.base) || 0, emoji: f.emoji.trim() || '🎯', deadline: f.deadline || null },
        { kw: f.kw.trim(), monthly: parseFloat(f.monthly) || 0, from: f.from || todayISO() });
      closeModal();
    } catch (e) { toast('שגיאה: ' + e.message); }
  }
  async function del() {
    if (!(await confirmDialog({ title: 'מחיקת יעד', body: 'ההעברות עצמן לא נמחקות, הן רק יחזרו לרשימת "לא שויכו".', okLabel: 'מחק', danger: true }))) return;
    try { await deleteSavingsGoal(g.id); closeModal(); } catch (e) { toast('⚠️ ' + e.message); }
  }
  return (
    <Sheet title={g ? 'עריכת יעד' : 'יעד חדש'}>
      <div className="form-row">
        <div><label className="lbl">שם היעד</label><input value={f.name} onChange={set('name')} placeholder="חופשה, רכב, קרן חירום" /></div>
        <div><label className="lbl">סכום יעד (₪)</label><input type="number" inputMode="decimal" min="0" value={f.target} onChange={set('target')} placeholder="20000" /></div>
      </div>
      <div className="form-row">
        <div><label className="lbl">תאריך יעד (רשות)</label><input type="date" value={f.deadline} onChange={set('deadline')} /></div>
        <div><label className="lbl">או סכום קבוע בחודש</label><input type="number" inputMode="decimal" value={f.monthly} onChange={set('monthly')} placeholder="1500" /></div>
      </div>
      <div className="note mb12">עם תאריך: האפליקציה מחשבת כמה צריך בחודש. בלי תאריך: הסכום הקבוע הוא היעד החודשי.</div>
      <label className="lbl">מילים שמזהות העברה ליעד בדף הבנק</label>
      <input value={f.kw} onChange={set('kw')} placeholder="פיקדון, הפקדה לחיסכון" />
      <div className="note mt6 mb12">מופרדות בפסיק. העברה מהעו"ש שהתיאור שלה מכיל אחת מהן נספרת ביעד לבד, בלי ללחוץ כלום.</div>
      <div className="form-row">
        <div><label className="lbl">כבר יש ביעד (₪)</label><input type="number" inputMode="decimal" value={f.base} onChange={set('base')} placeholder="0" /></div>
        <div><label className="lbl">לספור העברות מתאריך</label><input type="date" value={f.from} onChange={set('from')} /></div>
      </div>
      <div className="mb12"><label className="lbl">אמוג'י</label><input value={f.emoji} onChange={set('emoji')} placeholder="🎯" style={{ width: 80 }} /></div>
      <div className="row">
        <button className="btn btn-accent" style={{ flex: 1, padding: 11 }} onClick={save}>שמור יעד</button>
        {g && <button className="ubtn ubtn-outline danger-text" onClick={del} aria-label="מחק">🗑️</button>}
      </div>
    </Sheet>
  );
}
